import React from 'react';
import { Box, Flex, type BoxProps, type FlexProps } from '@chakra-ui/react';
import { Heading } from '$components/chakra/heading';
import { Fold, FoldInner } from './fold';

interface VariationProps {
  variation?: 'dark';
}

export function Inpage({ children, ...props }: BoxProps) {
  return (
    <Box
      as='article'
      display='grid'
      h='full'
      gridTemplateRows='min-content 1fr'
      {...props}
    >
      {children}
    </Box>
  );
}

export function InpageHeader({
  variation,
  children,
  ...props
}: BoxProps & VariationProps) {
  return (
    <Fold
      as='header'
      display='flex'
      flexDirection='row'
      flexWrap='nowrap'
      alignItems='flex-end'
      zIndex={1}
      bg='oci.base.50a'
      animation='oci-reveal 0.32s ease 0s 1'
      minH={
        variation === 'dark'
          ? { base: '16rem', md: '20rem', lg: '24rem', xl: '28rem' }
          : undefined
      }
      variation={variation}
      {...props}
    >
      {children}
    </Fold>
  );
}

// Same layout as FoldInner — retained for named import compatibility
export { FoldInner as InpageHeaderInner };

export function InpageCover({ children, ...props }: BoxProps) {
  return (
    <Box
      as='figure'
      position='absolute'
      top={0}
      left={0}
      right={0}
      bottom={0}
      zIndex={1}
      css={{
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          height: '100%',
          width: '100%',
          background: 'var(--oci-colors-oci-base-400a)',
          mixBlendMode: 'multiply',
          zIndex: 2
        }
      }}
      {...props}
    >
      {children}
    </Box>
  );
}

export function InpageCoverItem({ children, ...props }: BoxProps) {
  return (
    <Box
      as='figure'
      position='absolute'
      top={0}
      left={0}
      right={0}
      bottom={0}
      zIndex={1}
      css={{
        '& img': {
          position: 'relative',
          height: '100%',
          width: '100%',
          zIndex: 1,
          objectFit: 'cover'
        }
      }}
      {...props}
    >
      {children}
    </Box>
  );
}

export function InpageHeadline({ className, children, ...props }: BoxProps) {
  return (
    <Box
      className={
        className ? `oci-inpage-headline ${className}` : 'oci-inpage-headline'
      }
      position='relative'
      zIndex={3}
      display='flex'
      flexDirection='column'
      gap='4'
      gridColumn={{ base: '1 / span 4', md: '1 / span 6', lg: '1 / span 8' }}
      {...props}
    >
      {children}
    </Box>
  );
}

export function InpageDescription({ children, ...props }: FlexProps) {
  return (
    <Flex
      direction='column'
      gap='4'
      fontSize='lead'
      lineHeight='2rem'
      {...props}
    >
      {children}
    </Flex>
  );
}

export function InpageHeadNav({ children, ...props }: BoxProps) {
  return (
    <Box as='nav' fontSize='1rem' lineHeight='2rem' {...props}>
      {children}
    </Box>
  );
}

export function InpageTitle({ children, ...props }: BoxProps) {
  return (
    <Heading
      fontSize={{ base: '2rem', md: '2.25rem', lg: '2.5rem' }}
      lineHeight={{ base: '2.5rem', md: '2.75rem', lg: '3rem' }}
      {...props}
    >
      {children}
    </Heading>
  );
}

export function InpageHeadActions({ children, ...props }: BoxProps) {
  return (
    <Box
      position='relative'
      zIndex={3}
      display='flex'
      flexDirection='row'
      flexWrap='nowrap'
      gap='4'
      gridColumn={{ base: '1 / span -1', md: '7 / span 4', lg: '9 / span 4' }}
      alignItems={{ md: 'flex-end' }}
      justifyContent={{ md: 'flex-end' }}
      {...props}
    >
      {children}
    </Box>
  );
}

export function InpageBody({ children, ...props }: BoxProps) {
  return (
    <Box bg='transparent' {...props}>
      {children}
    </Box>
  );
}
